import React, { useState, useRef, lazy } from 'react';
import { motion, useScroll, useTransform, useSpring, useReducedMotion, type MotionValue } from 'framer-motion';
import { DottedLogo } from './DottedLogo';
import { useCopy } from './copy/copyMode';
import { SlackPanel } from './SlackIntegrationGraph';
export function LivesInSlack({
  progress
}: {
  progress?: MotionValue<number>;
}) {
  const copy = useCopy();
  const containerRef = useRef<HTMLDivElement>(null);
  const reducedMotion = useReducedMotion();
  const internalScroll = useScroll({
    target: containerRef,
    offset: ['start end', 'end start']
  });
  const scrollYProgress = progress || internalScroll.scrollYProgress;
  // Scroll-linked scale: the panel starts noticeably smaller and visibly
  // expands to full size as the section scrolls up into view, giving the beat
  // a bit of interactivity. It's anchored to the top so it only ever grows
  // downward (never rising over the headline). useTransform clamps outside the
  // input range, so it holds small before it enters and settles at full size
  // once the panel is comfortably in frame.
  const rawScale = useTransform(scrollYProgress, [0.08, 0.5], [0.8, 1.0]);
  const panelScale = useSpring(rawScale, {
    stiffness: 120,
    damping: 24,
    mass: 0.4
  });
  return <section className={`relative w-full overflow-hidden bg-[#FAF8F4] text-[#2B211A] ${progress ? 'h-screen flex flex-col justify-start pt-24 md:pt-28' : 'h-full pt-24 md:pt-28 pb-8 md:pb-20'}`} data-id="element-1983">
      
      {/* Grainy orange/dark gradient backdrop — the same uploaded abstract
                 texture used on the context-switch and "Giga noticed" beats. Heavily
                 blurred and muted under a cream wash so the texture only pools softly
                 toward the lower edges; the headline and Slack panel sit over the
                 lighter top/centre so they stay perfectly legible. */}
      <div aria-hidden="true" className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1984">
        
        <img src="/Untitled_design_(6)_1.png" alt="" className="absolute inset-0 h-full w-full object-cover" style={{
        filter: 'blur(40px) saturate(1.05)',
        transform: 'scale(1.2)'
      }} loading="lazy" data-id="element-1985" />
        
        {/* Cream wash — keeps the top and centre (headline + panel) calm and
         readable, letting the warm texture surface only toward the bottom. */}
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(180deg, #FAF8F4 0%, rgba(250,248,244,0.82) 30%, rgba(250,248,244,0.55) 62%, rgba(234,88,12,0.10) 100%)'
      }} data-id="element-1986" />
        
      </div>

      {/* Soft, flowy gradient wash — the gentle green pool at the lower-left. */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(90% 70% at 15% 90%, rgba(5,150,105,0.05) 0%, rgba(250,248,244,0) 60%)'
    }} data-id="element-1987" />
      

      {/* Dotted Giga logo background accent */}
      <DottedLogo variant="light" opacity={0.6} className="top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] md:w-[1200px] md:h-[1200px]" data-id="element-1988" />
      

      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 md:px-10 flex flex-col h-full" data-id="element-1989">
        <div className="text-center mb-6 md:mb-8 shrink-0" data-id="element-1990">
          {/* Heading spans the full centered section width (not the narrow
           paragraph column) and locks to a single line from tablet up. The
           size steps 4xl → 5xl so it always fits on one line at the md
           breakpoint without overflowing. Mobile keeps natural wrapping. */}
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-title font-medium leading-[1.1] tracking-tight text-[#2B211A] md:whitespace-nowrap" data-id="element-1991">
            Just @mention Giga in any chat
          </h2>
          <p className="mt-5 md:mt-6 max-w-2xl mx-auto text-base md:text-lg text-neutral-500 font-medium leading-relaxed" data-id="element-1992">
            It pulls answers from every tool you've connected and brings them
            right into the conversation — no tab-switching, no copy-paste.
          </p>
        </div>

        {/* The live Slack / Teams panel: toggles between platforms and hands
         back a generated PDF deliverable inline. Anchored to the top so the
         scroll-scale only ever grows it downward. */}
        <div ref={containerRef} className="relative z-10 mx-auto flex w-full max-w-[720px] flex-1 items-start justify-center" data-id="element-1993">
          
          <motion.div style={{
          scale: reducedMotion ? 1 : panelScale,
          transformOrigin: 'center top'
        }} className="w-full" data-id="element-1994">
            
            <SlackPanel data-id="element-1995" />
          </motion.div>
        </div>
      </div>
    </section>;
}